import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Home: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "PODO FEET - Maya Kälin",
    "description": "Professionelle Fusspflege in Altendorf - Ihre Füsse in guten Händen bei Maya Kälin, dipl. Fusspflegerin SFPV",
    "url": "https://podo-feet.ch",
    "telephone": "+41-XX-XXX-XX-XX",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Dorfplatz 6",
      "addressLocality": "Altendorf",
      "postalCode": "8852",
      "addressCountry": "CH"
    },
    "openingHours": "Mo-Fr 08:00-18:00",
    "image": "https://podo-feet.ch/images/portrait.jpg",
    "priceRange": "$$",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Fusspflege Dienstleistungen",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Medizinische Fusspflege",
            "description": "Nägel schneiden, feilen, Nagelfalz reinigen, Nagelhäutchen beseitigen, Hornhaut entfernen"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Nagelbehandlungen",
            "description": "Tamponade, Klebespangen bei eingewachsenen Nägeln sowie Nagelaufbau bei Pilzbefall"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Spezialbehandlungen",
            "description": "Hühneraugen und Schrunden entfernen, starke Hornhaut behandeln"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="PODO FEET - Professionelle Fusspflege in Altendorf | Maya Kälin SFPV"
        description="Professionelle Fusspflege in Altendorf - Ihre Füsse in guten Händen bei Maya Kälin, dipl. Fusspflegerin SFPV. Medizinische Fusspflege, Nagelbehandlungen und Spezialbehandlungen."
        keywords="Fusspflege Altendorf, Maya Kälin, SFPV, medizinische Fusspflege, Nagelbehandlung, Hühneraugen, Hornhaut, Podologie Schwyz"
        url="https://podo-feet.ch"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="relative bg-primary h-[500px] md:h-[600px] flex items-center">
        <div className="absolute inset-0 bg-black/40 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover z-0" 
          style={{ 
            backgroundImage: "url('/images/web-0664.jpg')", 
            backgroundPosition: "center 60%",
            transform: "scaleX(-1)"
          }}
        ></div>
        <div className="container-custom relative z-20 text-white">
          <div className="max-w-2xl">
            <h1 className="heading-1 mb-4">Willkommen bei PODO FEET</h1>
            <p className="text-lg md:text-xl mb-8">
              Professionelle Fusspflege in Altendorf - Ihre Füsse in guten Händen bei Maya Kälin, dipl. Fusspflegerin SFPV
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/kontakt" className="btn bg-white text-primary hover:bg-lightGreen">
                Termin vereinbaren
              </Link>
              <Link to="/preise" className="btn border-2 border-white hover:bg-white/20">
                Meine Leistungen
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Einleitung */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-2 text-primary mb-6">Professionelle Fusspflege in Altendorf</h2>
              <p className="mb-4">
                Bei PODO FEET erhalten Sie kompetente und einfühlsame Fusspflege durch Maya Kälin, diplomierte Fusspflegerin SFPV. 
                Mit langjähriger Erfahrung und regelmässigen Weiterbildungen garantiere ich Ihnen eine Behandlung auf höchstem Niveau.
              </p>
              <p className="mb-6">
                Gönnen Sie Ihren Füssen die Pflege, die sie verdienen und vertrauen Sie auf meine professionelle Expertise. 
                Ich freue mich darauf, Sie in meiner Praxis am Dorfplatz 6 in Altendorf begrüssen zu dürfen.
              </p>
              <Link to="/ueber-uns" className="btn btn-primary">
                Mehr über mich
              </Link>
            </div>
            <div className="relative">
              <img 
                src="/images/portrait.jpg" 
                alt="Maya Kälin - Dipl. Fusspflegerin SFPV" 
                className="w-full h-auto rounded-lg shadow-lg"
              />
              <div className="absolute -bottom-4 -right-4 p-4 bg-primary rounded-lg shadow-lg">
                <img 
                  src="/images/sfpv_badge.png" 
                  alt="SFPV Badge" 
                  className="w-24 h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leistungen Übersicht */}
      <section className="section bg-bgLight">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="heading-2 text-primary mb-4">Meine Leistungen</h2>
            <p className="max-w-3xl mx-auto">
              Bei PODO FEET biete ich Ihnen ein umfassendes Spektrum an Fusspflegeleistungen. 
              Von der klassischen medizinischen Fusspflege bis hin zur Behandlung von speziellen Fussbeschwerden - ich bin für Sie da.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Leistung 1 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-lightGreen rounded-full flex items-center justify-center mb-4 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-center mb-3">Fusspflege</h3>
              <p className="text-center text-gray-600">
                Nägel schneiden, feilen, Nagelfalz reinigen, Nagelhäutchen beseitigen, Hornhaut entfernen, Füsse eincremen.
              </p>
            </div>

            {/* Leistung 2 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-lightGreen rounded-full flex items-center justify-center mb-4 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-center mb-3">Nagelbehandlungen</h3>
              <p className="text-center text-gray-600">
                Tamponade, Klebespangen bei eingewachsenen Nägeln sowie Nagelaufbau/Nagelprothetik bei Pilzbefall oder Nagelverlust.
              </p>
            </div>

            {/* Leistung 3 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-lightGreen rounded-full flex items-center justify-center mb-4 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-center mb-3">Spezialbehandlungen</h3>
              <p className="text-center text-gray-600">
                Hühneraugen und Schrunden entfernen, starke Hornhaut behandeln sowie dekorative Nagellack-Anwendungen.
              </p>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/preise" className="btn btn-primary">
              Alle Leistungen ansehen
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonial / Zeitungsartikel */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-2 text-primary mb-6">Ausgezeichnete Fusspflege</h2>
              <p className="mb-4">
                2022 wurde ich im "Höfe Blättli" vorgestellt und durfte mich über die Auszeichnung als "Kopf des Monats" freuen. Ein schöner Moment für mich und eine Bestätigung meiner täglichen Arbeit mit meinen Kundinnen und Kunden.
              </p>
              <p className="mb-6">
                Seit über 6 Jahren kümmere ich mich nun schon um die Fussprobleme meiner Kundschaft. Die Dankbarkeit und Zufriedenheit meiner Kunden motiviert mich jeden Tag aufs Neue. Gerne helfe ich auch Ihnen zu schmerzfreien und gepflegten Füssen!
              </p>
              <Link to="/kontakt" className="btn btn-primary">
                Jetzt Termin vereinbaren
              </Link>
            </div>
            <div>
              <img 
                src="/images/scan_kopfdesmonats.png" 
                alt="Maya Kälin - Kopf des Monats im Höfe Blättli" 
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home; 